import { useQuery } from '@tanstack/react-query';

import { queryKeys } from '~/api/queryKeys';

import { getFunnelProducts } from '../services/run-funnel.service';

/** A title + thumbnail for one catalog product, as ⑨ reports it. */
export interface FunnelProductTitle {
  title: string | null;
  thumbUrl: string | null;
}

/** Most ids one ⑨ read is asked for (a page of the drill list). */
const MAX_IDS = 100;

/**
 * Titles/thumbs for a page of catalog product ids content-gen reads return bare (failures, QC
 * findings) — ONE ⑨ read by `productIds`, never one per row. Decoration only: an unavailable ⑨
 * (404/501 → null) leaves the rows on their ids.
 */
export function useFunnelProductTitles(runId: string, productIds: string[]) {
  const ids = [...new Set(productIds)].slice(0, MAX_IDS).sort();
  return useQuery({
    queryKey: queryKeys.catalogService.funnelProducts(runId, {
      productIds: ids,
      purpose: 'titles',
    }),
    queryFn: async () => {
      const matrix = await getFunnelProducts(runId, { productIds: ids, page: 1, size: ids.length });
      const map = new Map<string, FunnelProductTitle>();
      for (const row of matrix?.items ?? []) {
        if (row.productId) {
          map.set(row.productId, { title: row.title, thumbUrl: row.thumbUrl ?? row.imageUrl });
        }
      }
      return map;
    },
    enabled: !!runId && ids.length > 0,
    retry: false,
  });
}
